import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SolarScene from "@/components/SolarScene";
import ServiceRequestForm from "@/components/ServiceRequestForm";
import HomeownerQuoteForm from "@/components/HomeownerQuoteForm";
import { api, formatApiErrorDetail } from "@/lib/api";
import NotFound from "@/pages/NotFound";

const PRICING_STATUS_LABEL = {
  starting: "Starting price", typical: "Typical range", illustrative: "Illustrative",
  "member-only": "Member rate", "quote-required": "Quote required", fixed: "Published",
  free: "Free", upgrade: "Upgrade", historical: "Historical comparison", reference: "Reference", included: "Included",
};

/** Old routes from earlier site versions redirect to the new Coastal pages. */
const SERVICE_ALIASES = {
  home: "solar-panels", commercial: "commercial-solar", powerwall: "tesla-powerwall",
  "solar-roof": "tesla-solar-roof", roofing: "roofing-services", insulation: "spray-foam-insulation",
  ev: "ev-charger", "smart-panel": "span-smart-panel", supercharger: "tesla-supercharger",
  construction: "new-construction", care: "coastal-care-plan",
};

export default function CoastalServicePage() {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let alive = true;
    setState("loading");
    setContent(null);
    api.get(`/services-content/${slug}`)
      .then((r) => { if (alive) { setContent(r.data); setState("ready"); } })
      .catch(() => { if (alive) setState(SERVICE_ALIASES[slug] ? "alias" : "missing"); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (state !== "ready" || !content) return undefined;
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = "service-jsonld";
    node.text = JSON.stringify([
      {
        "@context": "https://schema.org", "@type": "Service",
        name: content.name, description: content.intro?.slice(0, 300),
        provider: { "@type": "Organization", name: "Solar Black Belt", telephone: "+1-310-881-5155", url: "https://commercialsolarroi.com" },
        areaServed: { "@type": "State", name: "Florida" },
        serviceType: content.name,
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://commercialsolarroi.com/" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://commercialsolarroi.com/services" },
          { "@type": "ListItem", position: 3, name: content.name, item: `https://commercialsolarroi.com${content.route}` },
        ],
      },
    ]);
    document.head.appendChild(node);
    return () => { document.getElementById("service-jsonld")?.remove(); };
  }, [state, content]);

  if (state === "loading") return null;
  if (state === "alias" && SERVICE_ALIASES[slug]) return <Navigate to={`/services/${SERVICE_ALIASES[slug]}`} replace />;
  if (state === "missing" || state === "alias") return <NotFound />;
  const related = (content.related || []).filter(Boolean);

  return (
    <>
      <Seo
        title={`${content.name} — Pricing, Scope & FAQs | Solar Black Belt`}
        description={`${content.name} in Florida: published planning prices with qualifiers, scope and timeline, coverage, and honest answers. Request a written quote from a solar specialist — 310-881-5155.`}
      />
      <section className="hero" style={{ padding: "52px 0 34px" }}>
        <div className="wrap">
          <p className="micro" style={{ marginBottom: 10 }}>
            <Link to="/">Home</Link> · <Link to="/services">Services</Link> · {content.name}
          </p>
          <div className="hero-grid" style={{ alignItems: "center" }}>
            <div className="hero-copy">
              <span className="eyebrow">{content.group.toUpperCase()} · FLORIDA</span>
              <h1 data-testid="service-heading" style={{ fontSize: "clamp(27px,3vw,42px)", letterSpacing: "-1.4px", lineHeight: 1.08 }}>{content.hero || content.name}</h1>
              <p data-testid="service-intro">{content.intro}</p>
              <div style={{ display: "flex", gap: 14, marginTop: 24, flexWrap: "wrap" }}>
                <a className="btn lime" href="#request-service" data-testid="service-estimate-cta">
                  Request my estimate <span>↗</span>
                </a>
                <a className="btn light" href="tel:+13108815155">Call 310-881-5155</a>
              </div>
            </div>
            <div className="hero-art service-art">
              <div className="scene-label"><span className="dot" /> INTERACTIVE ENERGY CONCEPT · DRAG TO ROTATE</div>
              <div style={{ height: "100%" }}>
                <SolarScene kind={content.sceneKind || "commercial"} state={{ [content.sceneKind || "commercial"]: true }} fallbackAlt={`Illustration of ${content.name.toLowerCase()} on a Florida property`} />
              </div>
              <div className="floating one">
                <span>{content.group.toUpperCase()}</span>
                <strong>{content.name}</strong>
                <span>Serving all of Florida</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <div className="city-grid">
            <div>
              <Reveal>
                <h2 style={{ fontSize: 24, margin: "0 0 10px" }}>What's included</h2>
                <ul className="benefitlist" data-testid="service-benefits">
                  {(content.benefits || []).map((b) => <li key={b}>{b}</li>)}
                </ul>
                <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Scope &amp; timeline</h2>
                <ul className="benefitlist" data-testid="service-scope">
                  {(content.scope || []).map((s) => <li key={s}>{s}</li>)}
                </ul>
              </Reveal>
              <Reveal delay={0.06}>
                <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Published pricing</h2>
                <div className="inc-wrap" style={{ maxWidth: 680 }}>
                  <table className="inc-table" data-testid="service-pricing">
                    <thead><tr><th>Item</th><th>Price</th><th>Conditions</th></tr></thead>
                    <tbody>
                      {(content.pricingRows || []).map((r) => (
                        <tr key={r.item}>
                          <td><b>{r.item}</b></td>
                          <td><span className="inc-tag" title={PRICING_STATUS_LABEL[r.status] || r.status}>{r.amount}</span></td>
                          <td>{r.conditions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="micro">Published planning prices collected October 23, 2026 from the installation provider. Final pricing depends on site conditions, equipment, electrical work, permitting and confirmed scope. Request a written quote.</p>
              </Reveal>
              <Reveal delay={0.06}>
                <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>{content.name} questions</h2>
                <div data-testid="service-faq">
                  {(content.faqs || []).map((f) => (
                    <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>
                  ))}
                </div>
              </Reveal>
              {related.length > 0 && (
                <Reveal delay={0.06}>
                  <h2 style={{ fontSize: 24, margin: "26px 0 10px" }}>Related services</h2>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="service-related">
                    {related.map((id) => <Link key={id} className="btn light" to={`/services/${id}`}>{id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} ↗</Link>)}
                  </div>
                </Reveal>
              )}
              <ServiceRequestForm content={content} />
              {["home", "solar-panels"].includes(content.id) && <HomeownerQuoteForm />}
            </div>
            <Reveal delay={0.1}>
              <aside className="report-preview" style={{ position: "sticky", top: 90 }}>
                <span className="eyebrow">AT A GLANCE</span>
                <h3>{content.name}</h3>
                <ul className="benefitlist" style={{ marginTop: 10 }}>
                  <li>Installed by licensed Florida crews (Roofing CCC1335978 · Electrical EC13013993 · HVAC CAC1816094)</li>
                  <li>Serving all of Florida — Fort Myers, Naples and Miami based</li>
                  <li>Written quotes, honest qualifiers, no invented prices</li>
                </ul>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
                  <a className="btn" href="#request-service">Request my estimate <span>↗</span></a>
                  <Link className="btn light" to="/live-quote">Free live quote (map review) ↗</Link>
                  <Link className="btn light" to="/projects">Jobs we've done ↗</Link>
                </div>
                <p className="micro" style={{ marginTop: 14 }}>Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform; Coastal Energy performs the project services. Solar Black Belt handles your quote and follow-up, led by Lawrence Williams.</p>
              </aside>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
